// /src/pages/CadastrarPlanoPage.jsx
// VERSÃO 4.3 ELITE - LOGÍSTICA DE INSTALAÇÃO E ARSENAL EXPANDIDO

import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from '../components/layout/MainLayout';
import MobileGuard from '../components/guards/MobileGuard';
import { 
    FiPlusCircle, FiCheckCircle, FiLoader, FiCpu, 
    FiTrash2, FiChevronLeft, FiChevronRight 
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { jwtDecode } from 'jwt-decode';
import { motion, AnimatePresence } from 'framer-motion';
import { ImovelExecutionEngine } from '../api/ImovelExecutionEngine';

import Etapa1Identificacao from '../components/imovel/cadastro/1EtapaIdentificacao';
import Etapa2Localizacao from '../components/imovel/cadastro/2EtapaLocalizacao';
import Etapa3AcervoDigital from '../components/imovel/cadastro/3EtapaAcervoDigital';
import Etapa4Especificacoes from '../components/imovel/cadastro/4EtapaEspecificacoesNarrativa';
import Etapa5FinanceiroSEO from '../components/imovel/cadastro/5EtapaFinanceiroSEO';

const STORAGE_KEY = "@tim_unity_plan_draft_v1";

const initialState = {
    nomeImobiliaria: 'Unity - Contabilizoom', 
    codigoImovel: '', 
    titulo: '', 
    descricao: '',
    creci: '',
    isDestaque: false, 
    seloDestaque: '', 
    tipoNegocio: 'Linhas novas', 
    tipoPrincipal: 'CNPJ', 
    subtipo: 'Correio', 
    nivelCobertura: 'Brasil',
    estadosCobertura: '', 
    cep: '', rua: '', numero: '', bairro: '', cidade: '', estado: '',
    quartos: 'Sim', suites: 'Sim', banheiros: 'Sim', vagasGaragem: '', areaUtil: '', areaTotal: 'Sim',
    valorVenda: 0, valorAluguel: 0, valorCondominio: 0, valorIptu: '',
    imagensDestaque: [], imagensQuartos: [], imagensBanheiros: [], imagensOutros: [],
    seoTitulo: '', seoDescricao: '', entidadeId: '', usuarioId: ''
};

// ⚔️ LISTA DE NEGÓCIOS (SYNC 2026)
const OPCOES_NEGOCIO = [
    { value: 'Linhas novas', label: 'Linhas novas' }, 
    { value: 'Portabilidade', label: 'Portabilidade' }, 
    { value: 'Renegociação 1', label: 'Renegociação 1' },
    { value: 'Renegociação 2', label: 'Renegociação 2' },
    { value: 'Transferência CPF para CNPJ', label: 'Transferência de titularidade CPF para CNPJ' },
    { value: 'Transferência Pre para Pós', label: 'Transferência Pre TIM para Pós' },
    { value: 'TIM FIBRA', label: 'TIM UltraFibra' },
    { value: 'M2M / IOT', label: 'M2M / IoT / Telemetria' },
    { value: 'Upgrade de Plano', label: 'Upgrade / Upgrade Turbo' }
];

const OPCOES_PRINCIPAL = [
    { value: 'CPF', label: 'CPF' }, 
    { value: 'CNPJ', label: 'CNPJ' },
    { value: 'Ambos', label: 'Ambos' }
];

// 🚚 ATIVAÇÃO / ENTREGA / INSTALAÇÃO
const SUBTIPOS_ENTREGA = [
    'Correio', 
    'Sedex', 
    'Transportadora TIM', 
    'Retirada', 
    'Motoboy', 
    'Ativação Digital (eSim)', 
    'Instalação Técnica' // ✅ NOVO ATIVO LOGÍSTICO
];

const PROMOCOES_ELITE = [
    { value: 'promocional', label: 'Promocional' },
    { value: 'melhor_custo_beneficio', label: 'Melhor Custo Benefício' },
    { value: 'mais_vendido', label: 'Mais Vendido' },
    { value: 'exclusivo_cnpj', label: 'Exclusivo CNPJ' }
];

export default function CadastrarImovelPage() {
    const navigate = useNavigate();
    const [currentStep, setCurrentStep] = useState(1);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false); 
    const [userRole, setUserRole] = useState('OPERADOR');

    const [formData, setFormData] = useState(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                return { ...initialState, ...parsed, imagensDestaque: [], imagensQuartos: [], imagensBanheiros: [], imagensOutros: [] };
            } catch (e) { return initialState; }
        }
        return initialState;
    });

    useEffect(() => {
        const dataToSave = { ...formData };
        ['imagensDestaque', 'imagensQuartos', 'imagensBanheiros', 'imagensOutros'].forEach(k => delete dataToSave[k]);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave));
    }, [formData]);

    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setUserRole(decoded.role?.toUpperCase() || 'USER');
                setFormData(prev => ({ 
                    ...prev, 
                    entidadeId: decoded.companyId || '6941b6eabcc7b127dc9e2951',
                    usuarioId: decoded.userId || ''
                }));
            } catch (err) { console.error("Falha no Token."); }
        }
    }, []);

    const handleChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'number' ? Number(value || 0) : value }));
    };

    const handleValueChange = useCallback((name, value) => setFormData(prev => ({ ...prev, [name]: value || 0 })), []);
    const handleAddressUpdate = useCallback((data) => setFormData(prev => ({ ...prev, ...data })), []);

    const nextStep = () => currentStep < 5 && setCurrentStep(prev => prev + 1);
    const prevStep = () => currentStep > 1 && setCurrentStep(prev => prev - 1);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!ImovelExecutionEngine.validarFaseFinal(formData)) return;
        setIsSubmitting(true); setIsProcessing(true); 
        try {
            const payload = ImovelExecutionEngine.prepararPayload(formData);
            const sucesso = await ImovelExecutionEngine.executarSincronizacao(payload);
            if (sucesso) {
                localStorage.removeItem(STORAGE_KEY);
                setTimeout(() => { navigate('/dashboard'); }, 2000);
            }
        } catch (err) { 
            setIsProcessing(false); setIsSubmitting(false); 
            toast.error("💥 Falha na Injeção!");
        }
    };

    const renderStep = () => {
        const props = { 
            formData, handleChange, handleValueChange, 
            currentSubtipos: SUBTIPOS_ENTREGA,
            onAddressFetch: handleAddressUpdate,
            opcoesNegocio: OPCOES_NEGOCIO,
            opcoesPrincipal: OPCOES_PRINCIPAL,
            promocoesElite: PROMOCOES_ELITE
        };
        switch (currentStep) {
            case 1: return <Etapa1Identificacao {...props} />;
            case 2: return <Etapa2Localizacao {...props} />;
            case 3: return <Etapa3AcervoDigital {...props} removeImage={(f, i) => setFormData({...formData, [f]: formData[f].filter((_, idx) => idx !== i)})} handleFileChange={(e) => setFormData({...formData, [e.target.name]: [...formData[e.target.name], ...Array.from(e.target.files)]})} />;
            case 4: return <Etapa4Especificacoes {...props} />;
            case 5: return <Etapa5FinanceiroSEO {...props} />;
            default: return null;
        }
    };

    return (
        <MobileGuard>
        <MainLayout>
            <AnimatePresence>{isProcessing && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[999] backdrop-blur-3xl flex flex-col items-center justify-center bg-black/90">
                    <FiCpu className="animate-spin mb-8 text-accent" size={100} style={{ color: 'var(--accent-color)' }} />
                    <h2 className="text-4xl font-black uppercase tracking-[0.8em] text-white">Sincronizando</h2>
                </motion.div>
            )}</AnimatePresence>

            <div className="w-full min-h-screen space-y-12 max-w-[1600px] mx-auto p-8 md:p-12 transition-all duration-500" style={{ backgroundColor: 'var(--bg-main)' }}>
                <header className="flex flex-col xl:flex-row items-center justify-between gap-10 border-b pb-12 relative overflow-hidden" style={{ borderColor: 'var(--border-color)' }}>
                    <div className="flex items-center gap-8 relative z-10">
                        <div className="w-20 h-20 rounded-[2.2rem] border shadow-2xl flex items-center justify-center bg-[var(--bg-card)] border-[var(--border-color)] text-accent"><FiPlusCircle size={32} /></div>
                        <div>
                            <h1 className="text-4xl md:text-5xl font-black tracking-tighter uppercase text-auto mb-2">Registro de <span className="text-accent">Plano TIM</span></h1>
                            <div className="flex items-center gap-4">
                                <div className="px-4 py-1.5 rounded-xl border border-auto bg-card-auto flex items-center gap-2">
                                    <div className="w-2 h-2 rounded-full bg-accent animate-pulse" />
                                    <span className="text-[9px] font-black uppercase tracking-widest opacity-60 text-auto">Etapa {currentStep} de 5</span>
                                </div>
                                <span className="text-[9px] font-black uppercase tracking-widest text-accent">Patente: {userRole}</span>
                            </div>
                        </div>
                    </div>
                    <div className="flex gap-3 relative z-10">
                        {[1, 2, 3, 4, 5].map(step => (
                            <div key={step} className={`h-2 w-12 md:w-20 rounded-full transition-all duration-500 ${step <= currentStep ? 'bg-accent shadow-[0_0_15px_rgba(var(--accent-rgb),0.4)]' : 'bg-white/5 border border-white/5'}`} />
                        ))}
                    </div>
                </header>

                <div className="flex flex-col lg:flex-row gap-12 items-start">
                    <main className="flex-1 w-full pb-60">
                        <AnimatePresence mode="wait">
                            <motion.div key={currentStep} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.4 }} className="card-elite p-10 md:p-16">
                                {renderStep()}
                            </motion.div>
                        </AnimatePresence>
                    </main>
                    <aside className="hidden xl:flex flex-col gap-6 w-80 sticky top-12">
                        <div className="card-elite p-8 space-y-6 text-center">
                            <p className="text-[10px] font-black opacity-30 uppercase tracking-[0.4em]">Integridade</p>
                            <p className="text-xs font-black uppercase text-accent mt-1">Draft Sincronizado</p>
                            <button onClick={() => {if(window.confirm("⚠️ DESTRUIR RASCUNHO?")){localStorage.removeItem(STORAGE_KEY); window.location.reload();}}} className="w-full py-4 rounded-2xl border border-red-500/20 text-red-500 hover:bg-red-500 hover:text-white transition-all font-black text-[10px] uppercase tracking-widest flex items-center justify-center gap-3">
                                <FiTrash2 size={14} /> Limpar Rascunho
                            </button>
                        </div>
                    </aside>
                </div>

                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 w-full max-w-[1200px] px-8 z-[100]">
                    <div className="backdrop-blur-3xl p-5 rounded-[2.5rem] border border-[var(--border-color)] shadow-[0_20px_50px_rgba(0,0,0,0.5)] flex justify-between items-center px-10" style={{ backgroundColor: 'var(--bg-card)' }}>
                        <button type="button" onClick={prevStep} disabled={currentStep === 1} className={`flex items-center gap-4 px-8 py-5 rounded-2xl font-black uppercase text-[10px] tracking-[0.3em] transition-all ${currentStep === 1 ? 'opacity-20 pointer-events-none' : 'hover:bg-white/5 text-auto'}`}>
                            <FiChevronLeft size={18} /> Voltar
                        </button>
                        {currentStep < 5 ? (
                            <button type="button" onClick={nextStep} className="group flex items-center gap-4 px-12 py-5 rounded-2xl bg-accent text-black font-black uppercase text-[10px] tracking-[0.3em] transition-all hover:scale-105 active:scale-95 shadow-xl">
                                Próxima Etapa <FiChevronRight size={18} />
                            </button>
                        ) : (
                            <button onClick={handleSubmit} disabled={isSubmitting} className="group relative flex items-center gap-4 px-16 py-5 rounded-2xl bg-green-500 text-black font-black uppercase text-[10px] tracking-[0.3em] transition-all hover:scale-105 active:scale-95 shadow-xl overflow-hidden">
                                {isSubmitting ? <FiLoader className="animate-spin" size={18} /> : <FiCheckCircle size={18} />}
                                {isSubmitting ? 'SINCRONIZANDO...' : 'EFETIVAR CADASTRO'}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </MainLayout>
        </MobileGuard>
    );
}